import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Stack,
  Grid,
  Card,
  Box,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Slide,
  Grow,
  keyframes,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CheckCircle } from "@mui/icons-material";
import {
  ClearCallLogoTitle,
  ClearCallLogo,
  ClearCallTitle,
} from "../../components/clear-call-logo";
import hospitalBackground from "../../assets/ward_back.png";
import heroImage from "../../assets/hero-image.png";
import forPatientsImage from "../../assets/for_patients.png";
import forCliniciansImage from "../../assets/for_clinicians.png";
import forServicesImage from "../../assets/for_services.png";
import forPayersImage from "../../assets/for_payers.png";
import iconAp1 from "../../assets/icon_ap_1.png";
import iconAp2 from "../../assets/icon_ap_2.png";
import iconAp3 from "../../assets/icon_ap_3.png";
import iconAp4 from "../../assets/icon_ap_4.png";
import iconAp5 from "../../assets/icon_ap_5.png";
import iconAp6 from "../../assets/icon_ap_6.png";
import iconAl1 from "../../assets/icon_al_1.png";
import iconAl2 from "../../assets/icon_al_2.png";
import iconAl3 from "../../assets/icon_al_3.png";
import iconAl4 from "../../assets/icon_al_4.png";
import iconAl5 from "../../assets/icon_al_5.png";
import iconDb1 from "../../assets/icon_db_1.png";
import iconDb2 from "../../assets/icon_db_2.png";
import iconDb3 from "../../assets/icon_db_3.png";
import iconDb4 from "../../assets/icon_db_4.png";
import iconDb5 from "../../assets/icon_db_5.png";
import iconFs1 from "../../assets/icon_fs_1.png";
import iconFs2 from "../../assets/icon_fs_2.png";
import iconFs3 from "../../assets/icon_fs_3.png";
import iconFs4 from "../../assets/icon_fs_4.png";
import challengeAfterLandscapeImage from "../../assets/challenge_after_landscape.png";
import challengeAfterLandscapeWideImage from "../../assets/challenge_after_landscape_wide.png";
import footImage from "../../assets/foot_diagram_cropped.jpg";
import rahLogo from "../../assets/RAH_logo.png";
import rahBackgroundImage from "../../assets/RAH.jpg";
import ivaiLogo from "../../assets/ivai_logo.png";
import southAustraliaLogo from "../../assets/south-australia-logo.svg";
import dotsPattern from "../../assets/dots.png";
import outbackBackground from "../../assets/outback.png";
import ribbonBackground from "../../assets/ribbon.png";
import couchBackground from "../../assets/couch.jpg";

const AboutPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isTouchDevice = useMediaQuery("(hover: none), (pointer: coarse)");
  const isBelowMd = !isMdUp;
  const useMobileImpactLayout = isTouchDevice;
  const [showHeader, setShowHeader] = useState(false);

  // Handle scroll to show/hide header
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const heroHeight = window.innerHeight * 0.8; // Approximate hero section height
      setShowHeader(scrollPosition > heroHeight);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Animation keyframes
  const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  `;

  const fadeInUp = keyframes`
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  `;

  const challengeSectionMinHeight: string = isMdUp
    ? "clamp(460px, 62vw, 540px)"
    : "auto";

  const challengeSectionBackgroundImage: string = isMdUp
    ? `linear-gradient(90deg, rgba(218, 220, 221, 0) 0%, rgba(243, 148, 84, 0) 40%, rgba(48, 173, 223, 0.56) 100%), url(${challengeAfterLandscapeWideImage})`
    : "none";

  const provenSectionMinHeight: string = isMdUp
    ? "clamp(460px, 62vw, 540px)"
    : "auto";

  const provenSectionBackgroundImage: string = isMdUp
    ? `linear-gradient(270deg, rgba(218, 220, 221, 0) 0%, rgba(243, 148, 84, 0) 40%, rgba(48, 223, 194, 0.82) 100%), url(${rahBackgroundImage})`
    : "none";

  return (
    <Stack sx={{ minHeight: "100vh", backgroundColor: "#ffffff" }}>
      {/* Sticky Header */}
      <Stack
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: "rgba(255, 255, 255, 0.65)",
          backdropFilter: "blur(10px)",
          zIndex: 1000,
          py: 1.5,
          px: 2,
          borderBottom: "1px solid rgba(25, 118, 210, 0.1)",
          transform: {
            xs: "translateY(0)",
            md: showHeader ? "translateY(0)" : "translateY(-100%)",
          },
          transition: "transform 0.3s ease-in-out",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Logo */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              sx={{
                cursor: "pointer",
              }}
            >
              <ClearCallLogo size={isMobile ? 24 : 32} />
              <ClearCallTitle size={isMobile ? 16 : 20} />
            </Stack>

            {/* Request Demo Button */}
            <Button
              component="a"
              href="mailto:contact@ivai.com.au?subject=Clear%20Call%20Demo%20Request"
              variant="contained"
              size={isMobile ? "small" : "medium"}
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 0.6, sm: 1 },
                fontSize: { xs: "0.8rem", sm: "0.95rem" },
                fontWeight: 600,
                borderRadius: 8,
                textTransform: "none",
                boxShadow: "0 4px 16px rgba(25, 118, 210, 0.3)",
                background: "linear-gradient(160deg, #19b6d2, #1565c0)",
                "&:hover": {
                  background: "linear-gradient(110deg, #19b6d2, #1592c0)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Request Demo
            </Button>
          </Stack>
        </Container>
      </Stack>
      {/* Hero Section */}
      <Stack
        direction="row"
        sx={{
          color: "#272827",
          py: 16,
          pb: { xs: 4, md: 16 },
          pt: { xs: 12, md: 16 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url(${hospitalBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.6,
            zIndex: 0,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(169, 207, 245, 0.22)",
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Fade in timeout={1000}>
            <Grid
              container
              spacing={4}
              alignItems="center"
              sx={{ minHeight: "60vh" }}
            >
              {/* Left side - Logo and Text */}
              <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 2, md: 1 } }}>
                <Stack
                  sx={{
                    textAlign: { xs: "center", md: "left" },
                    alignItems: { xs: "center", md: "flex-start" },
                  }}
                >
                  {/* Logo */}
                  <Stack
                    sx={{
                      mb: 6,
                      alignItems: "flex-start",
                      display: { xs: "none", md: "flex" },
                    }}
                  >
                    <ClearCallLogoTitle size={140} />
                  </Stack>

                  <Stack
                    sx={{ animation: `${fadeInUp} 1s ease-out 0.3s both` }}
                  >
                    <Typography
                      variant="h2"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        textAlign: { xs: "center", md: "left" },
                        fontSize: { xs: "1.5rem", sm: "2.2rem", md: "3.75rem" },
                      }}
                    >
                      A better telehealth experience for high-stakes clinical
                      decisions
                    </Typography>
                  </Stack>

                  <Stack
                    sx={{ animation: `${fadeInUp} 1s ease-out 0.6s both` }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 4,
                        lineHeight: 1.6,
                        textAlign: { xs: "center", md: "left" },
                        fontSize: { xs: ".9rem", sm: "1rem" },
                      }}
                    >
                      Clear Call provides specialists with the audio, visual,
                      and clinical detail they need to assess risk, guide care,
                      and decide on escalation remotely.
                    </Typography>
                  </Stack>

                  <Stack
                    sx={{
                      animation: `${fadeInUp} 1s ease-out 0.9s both`,
                      display: { xs: "none", md: "flex" },
                    }}
                  >
                    <Button
                      component="a"
                      href="mailto:contact@ivai.com.au?subject=Clear%20Call%20Demo%20Request"
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        maxWidth: "fit-content",
                        borderRadius: 10,
                        textTransform: "none",
                        boxShadow: "0 8px 32px rgba(25, 118, 210, 0.5)",
                        background: "linear-gradient(160deg, #19b6d2, #1565c0)",
                        "&:hover": {
                          background:
                            "linear-gradient(110deg,  #19b6d2, #1592c0)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 32px rgba(25, 118, 210, 0.5)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Request Demo
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
              {/* Right side - Image placeholder */}
              <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 2 } }}>
                <Stack sx={{ textAlign: "center" }}>
                  <Box
                    component="img"
                    src={heroImage}
                    alt="Clear Call hero visual"
                    sx={{
                      width: "100%",
                      maxHeight: { xs: 360, md: 600 },
                      objectFit: "contain",
                      borderRadius: 3,
                      filter: "drop-shadow(0 12px 28px rgba(0, 0, 0, 0.2))",
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Stack>

      {/* The Challenge */}
      <Container maxWidth={false} disableGutters>
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            minHeight: challengeSectionMinHeight,
            backgroundImage: challengeSectionBackgroundImage,
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "0 14px 40px rgba(0,0,0,0.2)",
          }}
        >
          <Box
            component="img"
            src={challengeAfterLandscapeImage}
            alt="Clinical consultation challenge"
            sx={{
              display: { xs: "block", md: "none" },
              width: "100%",
              height: { xs: 220, sm: 280 },
              objectFit: "cover",
            }}
          />
          <Stack
            sx={{
              position: "relative",
              zIndex: 2,
              minHeight: { xs: "auto", md: "inherit" },
              alignItems: "stretch",
              justifyContent: "center",
            }}
          >
            <Paper
              sx={{
                ml: { xs: 0, md: "50%" },
                minHeight: { xs: "auto", md: "inherit" },
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                p: { xs: 2.5, sm: 3.25, md: 8 },
                py: { xs: 6, md: 8 },
                borderRadius: 0,
                backgroundColor: "rgba(255, 255, 255, 0.53)",
                backdropFilter: "blur(4px)",
                boxShadow: "0 12px 36px rgba(0,0,0,0.16)",
              }}
            >
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  mb: 4,
                  textAlign: { xs: "center", md: "left" },
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "3.75rem" },
                }}
              >
                The Challenge
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  color: "text.primary",
                  fontWeight: 500,
                  textAlign: { xs: "center", md: "left" },
                  fontSize: { xs: "1rem", sm: "1.35rem", md: "1.5rem" },
                }}
              >
                <b>
                  High stakes clinical decisions often hinge on a single
                  question:
                </b>
                <br />
                <i>
                  “Can this patient be managed locally, or do they require
                  escalation to hospital-based care?”
                </i>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textAlign: { xs: "center", md: "left" },
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                Healthcare systems face mounting pressure. Rising chronic
                disease, ageing populations, and limited specialist capacity are
                pushing care toward unsustainable patterns. In high-stakes
                situations, timely specialist assessment prevents unnecessary
                transfers, avoids irreversible harm, and improves outcomes. Yet
                in many community and regional settings, clinicians still make
                these decisions under uncertainty.
                <br />
                <br />
                Standard telehealth tools are built for conversation, not
                diagnosis. They often lack the visual and clinical detail
                required for confident clinical judgement. This gap is not a
                failure of clinicians or knowledge. It is a failure of
                infrastructure. Clear Call was designed to address this
                constraint. It does not replace clinical expertise; it removes
                the geographical and informational barriers that limit it.
                <br />
                <br />
                When specialists can clearly see the patient and the procedure,
                decisions improve. Unnecessary escalation is reduced, and
                specialist capacity is preserved for patients who genuinely need
                it. The result is a more sustainable healthcare system and more
                equitable access to safe care, particularly for Aboriginal
                communities and rural populations.
              </Typography>
            </Paper>
          </Stack>
        </div>
      </Container>

      {/* Why Telehealth Falls Short */}
      <Stack
        sx={{
          background: "linear-gradient(160deg, #29b5ce57, #1f76b838)",
          py: { xs: 8, md: 14 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 2,
              mx: "auto",
              fontSize: { xs: "1.5rem", sm: "2rem", md: "3.75rem" },
            }}
          >
            Why Conventional Telehealth Falls Short
          </Typography>
          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              mb: 2,
              color: "text.secondary",
              fontSize: { xs: "1rem", sm: "1.35rem", md: "1.5rem" },
            }}
          >
            Communication does not equate to clinical certainty
          </Typography>

          <Typography
            variant="body1"
            paragraph
            sx={{
              textAlign: "center",
              maxWidth: "700px",
              mx: "auto",
              fontSize: { xs: "0.85rem", sm: "1rem" },
            }}
          >
            Conventional telehealth is built for conversation, not high-stakes
            clinical assessment. These gaps reduce confidence, weaken
            defensibility, and can drive unnecessary escalation.
          </Typography>

          <Grid container spacing={2} sx={{ mt: 2, mb: 0 }}>
            {[
              {
                subtitle: "Unreliable Audio-Visual",
                text: "Audio and video quality often drop below clinical standards, with muffling, distortion, and echo",
                icon: iconFs1,
                iconWidth: 76,
                iconMt: 0,
              },
              {
                subtitle: "Limited Visual Control",
                text: "Fixed camera perspectives can obscure clinically important details, limiting accurate assessment",
                icon: iconFs2,
                iconWidth: 76,
                iconMt: 0,
              },
              {
                subtitle: "Workflow Disruption",
                text: "Clinical image capture and annotation can interrupt workflow and may not meet privacy requirements",
                icon: iconFs3,
                iconWidth: 92,
                iconMt: 2,
              },
              {
                subtitle: "Patient Exclusion",
                text: "In remote consultations, patients, their families, and carers are often excluded from key parts of the discussion",
                icon: iconFs4,
                iconWidth: 76,
                iconMt: 1,
              },
            ].map((requirement, index) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 3 }}
                key={index}
                sx={{ pt: { xs: 2, sm: 2.5, md: 3 } }}
              >
                <Paper
                  sx={{
                    p: 2.5,
                    pt: { xs: 2.25, sm: 2.5, md: 3 },
                    minHeight: "unset",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: { xs: "stretch", md: "center" },
                    textAlign: { xs: "left", md: "center" },
                    position: "relative",
                    overflow: "visible",
                    background:
                      "linear-gradient(180deg, rgba(47, 236, 236, 0.35) 0%, rgba(0, 115, 168, 0.16) 100%)",
                    border: "1px solid rgba(53, 185, 204, 0.2)",
                    borderRadius: 3,
                    boxShadow: "0 8px 24px rgba(53, 185, 204, 0.12)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(180deg, rgba(47, 236, 236, 0.22) 0%, rgba(0, 115, 168, 0.08) 100%)",
                      border: "1px solid rgba(53, 185, 204, 0.3)",
                      boxShadow: "0 12px 36px rgba(53, 185, 204, 0.2)",
                    },
                  }}
                >
                  <Stack
                    direction={{ xs: "row", sm: "column" }}
                    spacing={{ xs: 2, sm: 1.5 }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    sx={{ width: "100%" }}
                  >
                    <Stack
                      sx={{
                        width: { xs: 72, sm: 82, md: 112 },
                        height: { xs: 72, sm: 82, md: 112 },
                        position: "relative",
                        mx: { xs: 0, sm: "auto" },
                        flexShrink: 0,
                        borderRadius: "50%",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid rgba(190, 229, 235, 0.55)",
                        background:
                          "linear-gradient(145deg, rgba(3, 160, 184, 1) 0%, rgba(0, 81, 143, 1) 100%)",
                        boxShadow:
                          "0 10px 24px rgba(15, 80, 129, 0.4), inset 0 0 0 1px rgba(53, 185, 204, 0.25)",
                      }}
                    >
                      <Box
                        component="img"
                        src={requirement.icon}
                        alt={`${requirement.text} icon`}
                        sx={{
                          width: {
                            xs: `${Math.round(requirement.iconWidth * 0.6)}px`,
                            sm: `${Math.round(requirement.iconWidth * 0.704)}px`,
                            md: `${requirement.iconWidth}px`,
                          },
                          mt: requirement.iconMt,
                          objectFit: "contain",
                          mixBlendMode: "screen",
                        }}
                      />
                    </Stack>

                    <Stack
                      sx={{
                        flex: 1,
                        textAlign: { xs: "left", sm: "center" },
                        pt: { xs: 0.25, sm: 0 },
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: 0.6,
                          color: "primary.main",
                          lineHeight: 1,
                          mb: 1,
                          mt: { xs: 0, sm: 0.5, md: 1 },
                          fontSize: {
                            xs: "0.62rem",
                            sm: "0.72rem",
                            md: "0.78rem",
                          },
                        }}
                      >
                        {requirement.subtitle}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.45,
                          fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        }}
                      >
                        {requirement.text}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Stack>

      {/* Clear Call Approach */}
      <Stack
        sx={{
          // background: "linear-gradient(160deg, #35b9cc, #1f75b8)",
          backgroundImage: `linear-gradient(160deg, rgba(0, 99, 102, 0.73), rgba(0, 76, 131, 0.93)), url(${ribbonBackground})`,
          // backgroundImage: `linear-gradient(160deg, rgba(0, 46, 48, 0.73), rgba(0, 36, 61, 0.93)), url(${ribbonBackground})`,
          backgroundBlendMode: "normal",
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          py: { xs: 8, md: 12 },
          pb: { xs: 4, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Stack>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{
                  textAlign: "center",
                  mb: 2,
                  color: "white",
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "3.75rem" },
                }}
              >
                The Clear Call Approach
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  textAlign: "center",
                  mb: 2,
                  color: "white",
                  fontSize: { xs: "1rem", sm: "1.35rem", md: "1.5rem" },
                }}
              >
                A workflow built for clinical judgement
                {/* Replacing uncertainty with a shared clinical perspective */}
              </Typography>
            </Stack>
          </Fade>

          <Slide direction="up" in timeout={1200}>
            <Typography
              variant="body1"
              paragraph
              sx={{
                mb: 4,
                textAlign: "center",
                mx: "auto",
                color: "white",
                maxWidth: "900px",
                fontSize: { xs: "0.85rem", sm: "1rem" },
              }}
            >
              Instead of working from partial information, specialists can see
              and hear what they need for reliable judgement.
              <br />
              Early deployments show this clarity is improving the speed and
              confidence of decision-making in community care.
            </Typography>
          </Slide>

          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
            {[
              {
                title: "Reliable, room-wide, multi-source audio",
                description:
                  "Audio is captured with clinical clarity using independent microphones for patients, carers, and clinicians. This approach reduces the muffling, distortion, and echo typical of general-purpose telehealth.",
                icon: (
                  <Box
                    component="img"
                    src={iconAp1}
                    alt="Audio feature icon"
                    sx={{
                      width: { xs: 64, sm: 80, md: 110 },
                      height: { xs: 64, sm: 80, md: 110 },
                      objectFit: "contain",
                    }}
                  />
                ),
                delay: 0,
              },
              {
                title: "Hands-free, first-person vision",
                description:
                  "On-site clinicians use a voice-controlled, hands-free headset to stream what they see. This allows specialists to guide the assessment and request specific angles or manoeuvres in real time.",
                icon: (
                  <Box
                    component="img"
                    src={iconAp4}
                    alt="Hands-free vision feature icon"
                    sx={{
                      width: { xs: 64, sm: 80, md: 110 },
                      height: { xs: 64, sm: 80, md: 110 },
                      objectFit: "contain",
                    }}
                  />
                ),
                delay: 200,
              },
              {
                title: "In-built instant high-resolution image capture",
                description:
                  "Clinical-grade images can be captured mid-consultation without disrupting workflow. The images can be annotated, downloaded, and shared in real time.",
                icon: (
                  <Box
                    component="img"
                    src={iconAp2}
                    alt="Image capture feature icon"
                    sx={{
                      width: { xs: 64, sm: 80, md: 110 },
                      height: { xs: 64, sm: 80, md: 110 },
                      objectFit: "contain",
                    }}
                  />
                ),
                delay: 400,
              },
              {
                title: "Optional 3D surface modelling",
                description:
                  "Three-dimensional wound measurements provide objective data for remote assessment. They enable precise measurement of depth and area.",
                icon: (
                  <Box
                    component="img"
                    src={iconAp5}
                    alt="3D surface modelling feature icon"
                    sx={{
                      width: { xs: 64, sm: 80, md: 110 },
                      height: { xs: 64, sm: 80, md: 110 },
                      objectFit: "contain",
                    }}
                  />
                ),
                delay: 600,
              },
              {
                title: "MODULAR DEPLOYMENT",
                description:
                  "Modules and hardware can be introduced in stages to match clinical need, workflow complexity, and service budget.",
                icon: (
                  <Box
                    component="img"
                    src={iconAp3}
                    alt="Modular capability feature icon"
                    sx={{
                      width: { xs: 64, sm: 80, md: 110 },
                      height: { xs: 64, sm: 80, md: 110 },
                      objectFit: "contain",
                    }}
                  />
                ),
                delay: 800,
              },
              {
                title: "CUSTOM INTERFACES FOR INDIVIDUAL ROLES",
                description:
                  "Interfaces are designed for specialists and on-site teams. They simplify workflows and ensure the right clinical information is visible when needed.",
                icon: (
                  <Box
                    component="img"
                    src={iconAp6}
                    alt="Custom interfaces feature icon"
                    sx={{
                      width: { xs: 64, sm: 80, md: 110 },
                      height: { xs: 64, sm: 80, md: 110 },
                      objectFit: "contain",
                    }}
                  />
                ),
                delay: 1000,
              },
            ].map((feature, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index} sx={{ display: "flex" }}>
                <Grow
                  in
                  timeout={1000}
                  style={{
                    transitionDelay: `${feature.delay}ms`,
                    width: "100%",
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      background:
                        "linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.06) 100%)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        // background:
                        //   "linear-gradient(145deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.2) 100%)",
                        background:
                          "linear-gradient(145deg, rgba(4, 4, 39, 0.35) 0%, rgba(4, 26, 41, 0.2) 100%)",
                        boxShadow: "0 20px 48px rgba(0,0,0,0.15)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        "& .feature-icon": {
                          animation: `${pulse} 1s ease-in-out`,
                        },
                      },
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems="center"
                      spacing={{ xs: 1.25, sm: 1.5 }}
                      sx={{
                        width: "100%",
                        textAlign: { xs: "center", sm: "left" },
                      }}
                    >
                      <Stack sx={{ color: "white", mixBlendMode: "screen" }}>
                        {feature.icon}
                      </Stack>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="overline"
                          sx={{
                            fontWeight: 600,
                            color: "white",
                            lineHeight: 1.3,
                            fontSize: {
                              xs: "0.68rem",
                              sm: "0.74rem",
                              md: "0.78rem",
                            },
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255,255,255,0.8)",
                            lineHeight: 1.5,
                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Stack>

      {/* Shines */}
      <Stack
        sx={{
          background: "linear-gradient(160deg, #35b8cc23, #1f76b838)",
          py: { xs: 6, md: 12 },
          pb: { xs: 2, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 2,
              fontSize: { xs: "1.5rem", sm: "2rem", md: "3.75rem" },
            }}
          >
            Where Clear Call Shines
          </Typography>
          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              mb: 4,
              color: "text.secondary",
              fontSize: { xs: "1rem", sm: "1.35rem", md: "1.5rem" },
            }}
          >
            A platform for multiple clinical pathways
          </Typography>

          <Typography
            variant="body1"
            paragraph
            sx={{
              mb: 4,
              textAlign: "center",
              maxWidth: "900px",
              mx: "auto",
              fontSize: { xs: "0.85rem", sm: "1rem" },
            }}
          >
            Clear Call provides the infrastructure for safer, faster clinical
            decisions. It gives local teams and specialists shared visibility of
            the same indicators, clearly and quickly. Shared visibility leads to
            more consistent care pathways. Unnecessary escalations drop and
            limited specialist capacity is preserved for the patients who need
            it most.
          </Typography>
          <Stack direction="column" spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
            {/* Decision Bottlenecks Stack */}
            <Paper
              sx={{
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 2.5, md: 3 },
                flex: 1,
                background:
                  "linear-gradient(145deg, rgba(223, 240, 253, 0.42) 0%, rgba(255, 255, 255, 0.28) 100%)",
                border: "1px solid rgba(255, 255, 255, 0.45)",
                borderRadius: 4,
                boxShadow:
                  "0 8px 24px rgba(31, 118, 184, 0.12), inset 0 1px 0 rgba(255,255,255,0.35)",
                position: "relative",
                overflow: "visible",
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  color: "rgba(11, 158, 202, 0.9)",
                  display: "block",
                  mb: 2,
                  textAlign: "center",
                  lineHeight: 1.3,
                  fontSize: { xs: "0.9rem", sm: "1.05rem", md: "1.25rem" },
                }}
              >
                Where there's Decision Bottlenecks such as
              </Typography>
              <Stack
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(5, minmax(0, 1fr))",
                  },
                  columnGap: { xs: 2, sm: 2.5, md: 3 },
                  rowGap: { xs: 3, sm: 3.5, md: 3 },
                  justifyItems: "center",
                  alignItems: "start",
                }}
              >
                {[
                  {
                    text: "Specialist expertise is centralised",
                    icon: iconDb1,
                    iconTop: "49%",
                    iconWidth: 120,
                  },
                  {
                    text: "Patients are remote, immobile, or hard to transfer",
                    icon: iconDb2,
                    iconTop: "49%",
                    iconWidth: 120,
                  },
                  {
                    text: "Visual and procedural assessment is critical",
                    icon: iconDb3,
                    iconTop: "49%",
                    iconWidth: 120,
                  },
                  {
                    text: "Escalation is costly, but delay carries clinical risk",
                    icon: iconDb4,
                    iconTop: "45%",
                    iconWidth: 120,
                  },
                  {
                    text: "Decisions depend on high-fidelity clinical information",
                    icon: iconDb5,
                    iconTop: "51%",
                    iconWidth: 120,
                  },
                ].map((condition, index) => (
                  <Stack
                    key={index}
                    alignItems="center"
                    sx={{
                      width: "100%",
                      height: "100%",
                      gap: { xs: 1.25, sm: 1.5, md: 1.5 },
                      gridColumn: {
                        xs: index === 4 ? "1 / -1" : "auto",
                        sm: index === 4 ? "2 / 3" : "auto",
                        md: "auto",
                      },
                      maxWidth: {
                        xs: index === 4 ? 160 : "none",
                        sm: "none",
                      },
                      justifySelf: index === 4 ? "center" : "stretch",
                      position: "relative",
                      cursor: "default",
                      "&:hover .db-circle": {
                        boxShadow:
                          "0 8px 32px rgba(53,185,204,0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
                      },
                    }}
                  >
                    <Stack
                      className="db-circle"
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{
                        width: { xs: 92, sm: 118, md: 140 },
                        height: { xs: 92, sm: 118, md: 140 },
                        borderRadius: "50%",
                        position: "relative",
                        overflow: "hidden",
                        background:
                          "linear-gradient(145deg, rgba(0,81,143,0.95) 0%, rgba(3,160,184,0.85) 100%)",
                        border: "2px solid rgba(190, 229, 235, 0.55)",
                        boxShadow:
                          "0 6px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Box
                        component="img"
                        src={condition.icon}
                        alt={`${condition.text} icon`}
                        sx={{
                          position: "absolute",
                          top: condition.iconTop ?? "45%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: {
                            xs: `${Math.round((condition.iconWidth ?? 120) * 0.58)}px`,
                            sm: `${Math.round((condition.iconWidth ?? 120) * 0.78)}px`,
                            md: `${condition.iconWidth ?? 120}px`,
                          },
                          objectFit: "contain",
                          mixBlendMode: "screen",
                        }}
                      />
                    </Stack>
                    <Typography
                      sx={{
                        mt: 0,
                        width: { xs: "96%", sm: "88%", md: "78%" },
                        color: "rgba(22, 34, 52, 0.92)",
                        fontWeight: 400,
                        textAlign: "center",
                        lineHeight: 1.2,
                        fontSize: {
                          xs: "0.76rem",
                          sm: "0.84rem",
                          md: "0.92rem",
                        },
                        letterSpacing: 0,
                      }}
                    >
                      {condition.text}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>

            {/* Applications Stack */}
            <Paper
              sx={{
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 2.5, md: 3 },
                flex: 1,
                background:
                  "linear-gradient(145deg, rgba(223, 240, 253, 0.42) 0%, rgba(255, 255, 255, 0.28) 100%)",
                border: "1px solid rgba(255, 255, 255, 0.45)",
                borderRadius: 4,
                boxShadow:
                  "0 8px 24px rgba(31, 118, 184, 0.12), inset 0 1px 0 rgba(255,255,255,0.35)",
                position: "relative",
                overflow: "visible",
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  color: "rgba(11, 158, 202, 0.9)",
                  display: "block",
                  mb: 2,
                  textAlign: "center",
                  lineHeight: 1.3,
                  fontSize: { xs: "0.9rem", sm: "1.05rem", md: "1.25rem" },
                }}
              >
                And in Applications such as
              </Typography>
              <Stack
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(5, minmax(0, 1fr))",
                  },
                  columnGap: { xs: 2, sm: 2.5, md: 3 },
                  rowGap: { xs: 3, sm: 3.5, md: 3 },
                  justifyItems: "center",
                  alignItems: "start",
                }}
              >
                {[
                  {
                    text: "Aged care escalation",
                    icon: iconAl1,
                    iconTop: "52%",
                    iconWidth: 120,
                  },
                  {
                    text: "Vascular services",
                    icon: iconAl2,
                    iconTop: "54%",
                    iconWidth: 120,
                  },
                  {
                    text: "Pressure injuries",
                    icon: iconAl3,
                    iconTop: "55%",
                    iconWidth: 120,
                  },
                  {
                    text: "Post-surgical review",
                    icon: iconAl4,
                    iconTop: "55%",
                    iconWidth: 120,
                  },
                  {
                    text: "Dermatology triage",
                    icon: iconAl5,
                    iconTop: "47%",
                    iconWidth: 120,
                  },
                ].map((application, index) => (
                  <Stack
                    key={index}
                    alignItems="center"
                    sx={{
                      width: "100%",
                      height: "100%",
                      gap: { xs: 1.25, sm: 1.5, md: 1.5 },
                      gridColumn: {
                        xs: index === 4 ? "1 / -1" : "auto",
                        sm: index === 4 ? "2 / 3" : "auto",
                        md: "auto",
                      },
                      maxWidth: {
                        xs: index === 4 ? 160 : "none",
                        sm: "none",
                      },
                      justifySelf: index === 4 ? "center" : "stretch",
                      position: "relative",
                      cursor: "default",
                      "&:hover .al-circle": {
                        boxShadow:
                          "0 8px 32px rgba(53,185,204,0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
                      },
                    }}
                  >
                    <Stack
                      className="al-circle"
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{
                        width: { xs: 92, sm: 118, md: 140 },
                        height: { xs: 92, sm: 118, md: 140 },
                        borderRadius: "50%",
                        position: "relative",
                        overflow: "hidden",
                        background:
                          "linear-gradient(145deg, rgba(0,81,143,0.95) 0%, rgba(3,160,184,0.85) 100%)",
                        border: "2px solid rgba(190, 229, 235, 0.55)",
                        boxShadow:
                          "0 6px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Box
                        component="img"
                        src={application.icon}
                        alt={`${application.text} icon`}
                        sx={{
                          position: "absolute",
                          top: application.iconTop ?? "45%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: {
                            xs: `${Math.round((application.iconWidth ?? 120) * 0.58)}px`,
                            sm: `${Math.round((application.iconWidth ?? 120) * 0.78)}px`,
                            md: `${application.iconWidth ?? 120}px`,
                          },
                          objectFit: "contain",
                          mixBlendMode: "screen",
                        }}
                      />
                    </Stack>
                    <Typography
                      sx={{
                        mt: 0,
                        width: { xs: "96%", sm: "88%", md: "78%" },
                        color: "rgba(22, 34, 52, 0.92)",
                        fontWeight: 400,
                        textAlign: "center",
                        lineHeight: 1.2,
                        fontSize: {
                          xs: "0.76rem",
                          sm: "0.84rem",
                          md: "0.92rem",
                        },
                        letterSpacing: 0,
                      }}
                    >
                      {application.text}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Stack>

      {/* System Impact */}
      <Stack
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 6, md: 0 },
          px: { xs: 2, sm: 3, md: 0 },
          "&::before": {
            content: '""',
            position: "absolute",
            top: -12,
            left: -12,
            right: -12,
            bottom: -12,
            zIndex: 0,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            // background:
            //   "linear-gradient(160deg, rgba(28, 170, 170, 0.63), rgba(31, 132, 190, 0.41))",
            background:
              "linear-gradient(160deg, rgba(176, 235, 235, 0.87), rgba(113, 196, 230, 0.86))",
            zIndex: 1,
          },
        }}
      >
        <Stack
          sx={{
            position: "relative",
            zIndex: 2,
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              textAlign: "center",
              color: "text.primary",
              fontSize: { xs: "1.5rem", sm: "2rem" },
              mb: 1,
            }}
          >
            Clear Call's Impact
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              color: "text.secondary",
              maxWidth: 420,
              fontSize: { xs: "0.85rem", sm: "0.95rem" },
            }}
          >
            Clear Call creates value across the care pathway, from patient
            experience to service efficiency.
          </Typography>
        </Stack>
        <Box
          component="div"
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: useMobileImpactLayout
              ? "column"
              : { xs: "column", md: "row" },
            width: "100%",
            gap: useMobileImpactLayout ? 2 : { xs: 2, md: 0 },
          }}
        >
          {[
            {
              key: "patients",
              title: "For patients",
              description:
                "Avoidance of stressful, costly, unnecessary transfers to metropolitan hospitals when local care is sufficient.",
              image: forPatientsImage,
            },
            {
              key: "clinicians",
              title: "For clinicians",
              description:
                "Immediate specialist support into rooms that previously operated in isolation, enabling safer and more confident clinical decisions",
              image: forCliniciansImage,
            },
            {
              key: "health-services",
              title: "For health services",
              description:
                "Reduces avoidable admissions and preserves scarce specialist time that would otherwise be consumed by travel or low-value in-person reviews.",
              image: forServicesImage,
            },
            {
              key: "payers",
              title: "For payers",
              description:
                "Repeatable and measurable efficiency driven by fewer transports, fewer bed days, and more timely escalation",
              image: forPayersImage,
            },
          ].map((impact) => (
            <Box
              component="div"
              key={impact.key}
              sx={{
                flex: useMobileImpactLayout
                  ? "1 1 auto"
                  : { xs: "1 1 auto", md: "0 0 25%" },
                position: "relative",
                overflow: "hidden",
                height: useMobileImpactLayout ? "auto" : { xs: "auto", md: 480 },
                cursor: "default",
                borderRadius: useMobileImpactLayout ? 4 : { xs: 4, md: 0 },
                boxShadow: useMobileImpactLayout
                  ? "0 18px 40px rgba(15, 80, 129, 0.18)"
                  : {
                    xs: "0 18px 40px rgba(15, 80, 129, 0.18)",
                    md: "none",
                  },
                background: useMobileImpactLayout
                  ? "linear-gradient(145deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.08))"
                  : {
                    xs: "linear-gradient(145deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.08))",
                    md: "transparent",
                  },
                border: useMobileImpactLayout
                  ? "1px solid rgba(255, 255, 255, 0.32)"
                  : {
                    xs: "1px solid rgba(255, 255, 255, 0.32)",
                    md: "none",
                  },
                backdropFilter: useMobileImpactLayout
                  ? "blur(10px)"
                  : { xs: "blur(10px)", md: "none" },
                WebkitBackdropFilter: useMobileImpactLayout
                  ? "blur(10px)"
                  : { xs: "blur(10px)", md: "none" },
                borderBottom: {
                  xs: "none",
                  md: "none",
                },
                "@media (hover: hover) and (pointer: fine)": {
                  "&:hover .impact-overlay": {
                    opacity: 1,
                  },
                  "&:hover .impact-img": {
                    transform: "scale(1.06)",
                  },
                },
              }}
            >
              <Box
                className="impact-img"
                component="img"
                src={impact.image}
                alt={impact.title}
                sx={{
                  width: "100%",
                  height: useMobileImpactLayout
                    ? { xs: 220, sm: 280, md: 320 }
                    : { xs: 220, sm: 280, md: "100%" },
                  objectFit: "cover",
                  display: "block",
                  transition: "transform 0.45s ease",
                  borderTopLeftRadius: useMobileImpactLayout ? 16 : { xs: 16, md: 0 },
                  borderTopRightRadius: useMobileImpactLayout ? 16 : { xs: 16, md: 0 },
                }}
              />
              {/* Gradient + title always visible at bottom */}
              <Box
                component="div"
                sx={{
                  display: useMobileImpactLayout ? "none" : { xs: "none", md: "block" },
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  pt: 10,
                  pb: 3,
                  px: 3,
                  background:
                    "linear-gradient(transparent, rgba(31, 31, 31, 0.82))",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    color: "white",
                    lineHeight: 1,
                  }}
                >
                  {impact.title}
                </Typography>
              </Box>
              {/* Hover overlay with description */}
              <Box
                component="div"
                className="impact-overlay"
                sx={{
                  position: useMobileImpactLayout
                    ? "relative"
                    : { xs: "relative", md: "absolute" },
                  top: useMobileImpactLayout ? "auto" : { md: 0 },
                  left: useMobileImpactLayout ? "auto" : { md: 0 },
                  right: useMobileImpactLayout ? "auto" : { md: 0 },
                  bottom: useMobileImpactLayout ? "auto" : { md: 0 },
                  background: useMobileImpactLayout
                    ? "rgba(255, 255, 255, 0.72)"
                    : {
                      xs: "rgba(255, 255, 255, 0.72)",
                      md: "linear-gradient(rgba(0, 120, 110, 0.84), rgba(0, 80, 120, 0.84))",
                    },
                  backdropFilter: useMobileImpactLayout
                    ? "blur(14px)"
                    : { xs: "blur(14px)", md: "blur(4px)" },
                  WebkitBackdropFilter: useMobileImpactLayout
                    ? "blur(14px)"
                    : { xs: "blur(14px)", md: "blur(4px)" },
                  opacity: useMobileImpactLayout ? 1 : { xs: 1, md: 0 },
                  transition: "opacity 0.35s ease",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: useMobileImpactLayout
                    ? "flex-start"
                    : { xs: "flex-start", md: "center" },
                  justifyContent: useMobileImpactLayout
                    ? "flex-start"
                    : { xs: "flex-start", md: "center" },
                  px: useMobileImpactLayout
                    ? { xs: 2.25, sm: 2.75, md: 2.75 }
                    : { xs: 2.25, sm: 2.75, md: 4 },
                  pt: useMobileImpactLayout
                    ? { xs: 2, sm: 2.4, md: 2.4 }
                    : { xs: 2, sm: 2.4, md: 4 },
                  pb: useMobileImpactLayout
                    ? { xs: 0.8, sm: 1, md: 1 }
                    : { xs: 0.8, sm: 1, md: 4 },
                  mt: useMobileImpactLayout
                    ? { xs: -5, sm: -4, md: -4 }
                    : { xs: -5, sm: -4, md: 0 },
                  mx: { xs: 0, md: 0 },
                  borderTopLeftRadius: { xs: 0, md: 0 },
                  borderTopRightRadius: { xs: 0, md: 0 },
                  borderBottomLeftRadius: useMobileImpactLayout ? 16 : { xs: 16, md: 0 },
                  borderBottomRightRadius: useMobileImpactLayout ? 16 : { xs: 16, md: 0 },
                  boxShadow: useMobileImpactLayout
                    ? "0 10px 28px rgba(8, 39, 69, 0.2)"
                    : {
                      xs: "0 10px 28px rgba(8, 39, 69, 0.2)",
                      md: "none",
                    },
                  zIndex: 3,
                  minHeight: { xs: "auto", md: "auto" },
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    color: useMobileImpactLayout
                      ? "rgba(13, 48, 74, 0.95)"
                      : { xs: "rgba(13, 48, 74, 0.95)", md: "white" },
                    lineHeight: 1,
                    mb: 1.25,
                    textAlign: useMobileImpactLayout
                      ? "left"
                      : { xs: "left", md: "center" },
                    fontSize: useMobileImpactLayout
                      ? { xs: "0.72rem", sm: "0.78rem", md: "0.78rem" }
                      : { xs: "0.72rem", sm: "0.78rem", md: "0.82rem" },
                  }}
                >
                  {impact.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: useMobileImpactLayout
                      ? "rgba(15, 53, 80, 0.86)"
                      : {
                        xs: "rgba(15, 53, 80, 0.86)",
                        md: "rgba(255, 255, 255, 0.9)",
                      },
                    textAlign: useMobileImpactLayout
                      ? "left"
                      : { xs: "left", md: "center" },
                    lineHeight: 1.6,
                    mt: useMobileImpactLayout ? 1 : { xs: 1, md: 0 },
                    fontSize: useMobileImpactLayout
                      ? { xs: "0.82rem", sm: "0.9rem", md: "0.9rem" }
                      : { xs: "0.82rem", sm: "0.9rem" },
                  }}
                >
                  {impact.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Stack>

      {/* Proven in Practice */}
      <Container maxWidth={false} disableGutters>
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            minHeight: provenSectionMinHeight,
            backgroundImage: provenSectionBackgroundImage,
            backgroundSize: "cover",
            backgroundPosition: "bottom center",
            boxShadow: "0 14px 40px rgba(0,0,0,0.2)",
          }}
        >
          <Box
            component="img"
            src={rahBackgroundImage}
            alt="Royal Adelaide Hospital"
            sx={{
              display: { xs: "block", md: "none" },
              width: "100%",
              height: { xs: 220, sm: 280 },
              objectFit: "cover",
              objectPosition: "bottom center",
            }}
          />
          <Stack
            sx={{
              position: "relative",
              zIndex: 2,
              minHeight: { xs: "auto", md: "inherit" },
              alignItems: "stretch",
              justifyContent: "center",
            }}
          >
            <Paper
              sx={{
                mr: { xs: 0, md: "50%" },
                minHeight: { xs: "auto", md: "inherit" },
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                p: { xs: 2.5, sm: 3.25, md: 8 },
                py: { xs: 4.5, md: 8 },
                borderRadius: 0,
                backgroundColor: "rgba(255, 255, 255, 0.63)",
                backdropFilter: "blur(8px)",
                boxShadow: "0 12px 36px rgba(0,0,0,0.16)",
              }}
            >
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  mb: 4,
                  textAlign: { xs: "center", md: "left" },
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "3.75rem" },
                }}
              >
                Proven in Practice
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  mb: 3,
                  color: "text.primary",
                  textAlign: { xs: "center", md: "left" },
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                Clear Call is currently being trialled in the multidisciplinary
                foot clinic at the Royal Adelaide Hospital, supporting decisions
                about local management versus urgent escalation. In practice,
                Clear Call enables:
              </Typography>
              <List sx={{ mb: 0.5 }}>
                {[
                  "Structured remote assessment with shared clinical context",
                  "Earlier escalation when clinical risk is identified",
                  "Safe local management when hospital transfer is not required",
                  "Greater confidence between local and metropolitan teams",
                  "More consistent, defensible decision-making at the point of care",
                  "A more engaging and enjoyable patient-clinician interaction",
                ].map((outcome, index) => (
                  <ListItem
                    disableGutters
                    key={index}
                    sx={{
                      py: { xs: 0.9, sm: 1.1, md: 1.25 },
                      px: { xs: 1.5, sm: 2 },
                      mb: { xs: 1, md: 1.5 },
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, rgba(0, 207, 179, 0.08) 0%, rgba(0, 159, 134, 0.04) 100%)",
                      border: "1px solid rgba(0, 207, 179, 0.2)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, rgba(0, 207, 179, 0.12) 0%, rgba(0, 159, 134, 0.08) 100%)",
                        border: "1px solid rgba(0, 207, 179, 0.4)",
                        transform: "translateX(4px)",
                        boxShadow: "0 4px 12px rgba(0, 207, 179, 0.15)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "auto",
                        mr: { xs: 1.25, sm: 2, md: 2.5 },
                      }}
                    >
                      <CheckCircle
                        sx={{
                          fontSize: { xs: 20, sm: 24, md: 28 },
                          color: "#00cfb3",
                          transition: "all 0.3s ease",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={outcome}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 600,
                        sx: {
                          color: "text.primary",
                          fontSize: {
                            xs: "0.8rem",
                            sm: "0.9rem",
                            md: "0.95rem",
                          },
                          lineHeight: { xs: 1.4, md: 1.5 },
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Stack>
        </div>
      </Container>

      {/* Footer */}
      <Stack
        component="a"
        href="https://ivai.com.au"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          background: "linear-gradient(135deg, #414141 0%, #1a1a1a 100%)",
          color: "white",
          py: 2,
          position: "relative",
          textDecoration: "none",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url(${dotsPattern})`,
            backgroundRepeat: "repeat",
            backgroundSize: "75%",
            opacity: 0.25,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={3}
            sx={{
              width: "100%",
              color: "inherit",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
              transition: "opacity 0.3s ease",
            }}
          >
            <Box
              component="img"
              src={ivaiLogo}
              alt="IVAI Logo"
              sx={{
                height: 32,
                width: "auto",
                maxWidth: "32%",
                objectFit: "contain",
              }}
            />
            <Box
              component="img"
              src={southAustraliaLogo}
              alt="South Australia Logo"
              sx={{
                height: { xs: 19.2, sm: 32 },
                width: "auto",
                maxWidth: { xs: "19.2%", sm: "32%" },
                objectFit: "contain",
              }}
            />
          </Stack>
        </Container>
      </Stack>
    </Stack>
  );
};

export default AboutPage;
